import { NO_VALUE, count, money, ratio, safeDivide } from "../../performanceSnapshot"

/*
    Per-ad metrics for the Ads tab (MPN-1530 §6.1, §6.2).

    CPC, CPM and CTR are derivations of numbers ad-performance already returns, so nothing
    new is fetched for them. Days Scheduled comes from ad-schedule-spans, which is the same
    resolver §6.4's bars and MPN-1531's attribution read, so an ad's day count and its bar
    can never disagree.

    Avg Daily Impressions is the metric with the most room to mislead. It divides by days
    SCHEDULED, not by days elapsed, which is the point: an ad that ran hard for six days
    and one that trickled for sixty can show identical impression totals, and only this
    separates them. Dividing by an ad's outer schedule window instead of the days it
    actually ran would flatter the short one.
*/

export type AdRow = {
  ad_id: number
  ad_name: string
  ad_type?: string | null
  spend: number
  impressions: number
  clicks: number
  engagements: number
  daysScheduled: number
  cpc: string
  cpm: string
  ctr: string
  avgDailyImpressions: string
  conversionsDuringFlight: number | null
}

const num = (v: unknown) => {
  const n = typeof v === "number" ? v : Number(v ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function adRows(charts: any): AdRow[] {
  const performance: any[] = charts?.["ad-performance"] ?? []
  const spans: any[] = charts?.["ad-schedule-spans"] ?? []
  const flight: any[] = charts?.["ad-flight-conversions"] ?? []

  const daysById = new Map<number, number>(
    spans.map((s) => [s.ad_id, num(s.days_scheduled)]),
  )
  const conversionsById = new Map<number, number>(
    flight.map((f) => [f.ad_id, num(f.conversions_during_flight)]),
  )

  return performance.map((ad) => {
    const spend = num(ad.total_spend)
    const impressions = num(ad.total_impressions)
    const clicks = num(ad.total_clicks)
    const daysScheduled = daysById.get(ad.ad_id) ?? 0

    const cpc = safeDivide(spend, clicks)
    const cpm = safeDivide(spend * 1000, impressions)
    const ctr = safeDivide(clicks * 100, impressions)
    const avgDaily = safeDivide(impressions, daysScheduled)

    return {
      ad_id: ad.ad_id,
      ad_name: ad.ad_name || "Untitled Ad",
      ad_type: ad.ad_type,
      spend,
      impressions,
      clicks,
      engagements: num(ad.total_engagements),
      daysScheduled,
      // A dash rather than $0.00 wherever the denominator is zero (§6.1): "no clicks
      // yet" and "clicks were free" are different claims.
      cpc: cpc === null ? NO_VALUE : money(cpc),
      cpm: cpm === null ? NO_VALUE : money(cpm),
      ctr: ctr === null ? NO_VALUE : `${ratio(ctr)}%`,
      avgDailyImpressions: avgDaily === null ? NO_VALUE : count(avgDaily),
      conversionsDuringFlight: conversionsById.has(ad.ad_id)
        ? conversionsById.get(ad.ad_id)!
        : null,
    }
  })
}

export type LeaderboardBasis = "engagements" | "conversions"

/**
 * Top three ads (§6.2).
 *
 * By Conversions ranks on "conversions during flight" (MPN-1531), which counts every
 * conversion that happened while an ad was live. Two ads running the same days both count
 * the same conversion, so these do not sum to the campaign total and are not a claim that
 * the ad caused anything.
 */
export function leaderboard(rows: AdRow[], basis: LeaderboardBasis): AdRow[] {
  const ranked = [...rows].sort((a, b) =>
    basis === "conversions"
      ? (b.conversionsDuringFlight ?? -1) - (a.conversionsDuringFlight ?? -1)
      : b.engagements - a.engagements,
  )
  return ranked.slice(0, 3)
}
